import { ActionIcon, Button, FileButton } from '@mantine/core';
import { Icon, sizes, variants } from '@uag/react-core';
import { useCallback, useMemo, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { CopilotRequestSettings } from 'app/modules/wallforming/components/copilot/CopilotRequestSettings';
import {
    formatMegabytes,
    isPdfFile,
    isPlanFile,
    MAX_PLAN_FILES,
    MAX_PLAN_TOTAL_BYTES,
    PLAN_FILE_ACCEPT
} from 'app/modules/wallforming/components/plan-to-ifc/utils/planToIfcUtils';

import type { useCopilotRequestSettings } from 'app/modules/wallforming/components/copilot/hooks/useCopilotRequestSettings';

interface PlanUploadFormProps {
    isSubmitting: boolean;
    requestSettings: ReturnType<typeof useCopilotRequestSettings>;
    onGenerate: (name: string, files: File[], notes: string, autoForm: boolean) => void;
}

/** Name, files and one primary action; model selection and notes stay folded away until needed. */
export const PlanUploadForm = ({ isSubmitting, requestSettings, onGenerate }: PlanUploadFormProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [notes, setNotes] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [autoForm, setAutoForm] = useState(true);
    const [isDragging, setIsDragging] = useState(false);

    const totalBytes = useMemo(() => files.reduce((sum, file) => sum + file.size, 0), [files]);
    const validationMessage = useMemo(() => {
        if (files.some((file) => !isPlanFile(file))) {
            return t('infoPlanToIfcUnsupportedFile');
        }
        if (files.length > MAX_PLAN_FILES) {
            return t('infoPlanToIfcTooManyFiles', { max: MAX_PLAN_FILES });
        }
        return totalBytes > MAX_PLAN_TOTAL_BYTES
            ? t('infoPlanToIfcTooLarge', { max: formatMegabytes(MAX_PLAN_TOTAL_BYTES) })
            : null;
    }, [files, t, totalBytes]);
    const canGenerate =
        name.trim().length > 0 &&
        files.length > 0 &&
        validationMessage === null &&
        !isSubmitting &&
        requestSettings.isValid;

    const addFiles = useCallback((selected: File[]) => {
        setFiles((current) => {
            const combined = [...current];
            for (const file of selected) {
                if (!combined.some((existing) => existing.name === file.name && existing.size === file.size)) {
                    combined.push(file);
                }
            }
            return combined;
        });
    }, []);

    const handleDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDragging(false);
            addFiles(Array.from(event.dataTransfer.files));
        },
        [addFiles]
    );

    return (
        <div className="space-y-3">
            <label className="block text-xs font-medium text-text-light">
                {t('labelBuildingName')}
                <input
                    aria-label="planToIfcName"
                    className="mt-1 h-8 w-full rounded-sm border border-border-light px-2 text-sm text-text-default outline-none focus:border-border-accent"
                    disabled={isSubmitting}
                    maxLength={200}
                    placeholder={t('placeholderBuildingName')}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                />
            </label>

            <div
                className={`rounded-md border border-dashed p-3 text-center transition-colors ${
                    isDragging ? 'border-border-accent bg-background-gray-ultra-light' : 'border-border-light'
                }`}
                data-testid="plan-to-ifc-dropzone"
                onDragLeave={() => setIsDragging(false)}
                onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                }}
                onDrop={handleDrop}
            >
                <div className="flex items-center justify-center gap-2 text-sm text-text-light">
                    <Icon icon="upload_file" />
                    {t('infoPlanToIfcDrop')}
                    <FileButton accept={PLAN_FILE_ACCEPT} disabled={isSubmitting} multiple onChange={addFiles}>
                        {(props) => (
                            <Button {...props} size={sizes.button.small} variant={variants.button.secondary}>
                                {t('buttonAddPdfs')}
                            </Button>
                        )}
                    </FileButton>
                </div>
                <div className="mt-1 text-xs text-text-light">
                    {t('infoPlanToIfcLimits', {
                        maxFiles: MAX_PLAN_FILES,
                        maxSize: formatMegabytes(MAX_PLAN_TOTAL_BYTES)
                    })}
                </div>
            </div>

            {files.length > 0 && (
                <div>
                    <ul className="max-h-36 space-y-1 overflow-auto" data-testid="plan-to-ifc-files">
                        {files.map((file, index) => (
                            <li
                                key={`${file.name}-${file.size}`}
                                className="flex items-center justify-between rounded-sm bg-background-gray-ultra-light px-2 py-0.5"
                            >
                                <span className="flex min-w-0 items-center gap-1 text-sm">
                                    <Icon icon={isPdfFile(file) ? 'picture_as_pdf' : 'image'} size="sm" />
                                    <span className="truncate">{file.name}</span>
                                </span>
                                <span className="flex shrink-0 items-center gap-1 text-xs text-text-light">
                                    {formatMegabytes(file.size)}
                                    <ActionIcon
                                        aria-label={t('buttonRemove')}
                                        className="action-icon-small bg-transparent hover:bg-background-gray! icon-sm"
                                        disabled={isSubmitting}
                                        onClick={() =>
                                            setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))
                                        }
                                    >
                                        close
                                    </ActionIcon>
                                </span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-1 text-xs text-text-light">
                        {t('infoPlanToIfcSelection', {
                            count: files.length,
                            max: MAX_PLAN_FILES,
                            size: formatMegabytes(totalBytes)
                        })}
                    </div>
                </div>
            )}
            {validationMessage && (
                <div className="text-xs" style={{ color: '#b45309' }}>
                    {validationMessage}
                </div>
            )}

            <details className="rounded-sm border border-border-light px-2 py-1" data-testid="plan-to-ifc-options">
                <summary className="cursor-pointer text-xs font-medium text-text-light">
                    {t('labelPlanToIfcOptions')}
                </summary>
                <div className="mt-2 space-y-2 pb-1">
                    <CopilotRequestSettings disabled={isSubmitting} settings={requestSettings} showTimeout={false} />
                    <label className="block text-xs text-text-light">
                        {t('labelNotes')} {t('infoOptional')}
                        <textarea
                            aria-label="planToIfcNotes"
                            className="mt-1 w-full rounded-sm border border-border-light px-2 py-1 text-sm text-text-default outline-none focus:border-border-accent"
                            disabled={isSubmitting}
                            maxLength={2000}
                            placeholder={t('placeholderPlanToIfcNotes')}
                            rows={3}
                            style={{ resize: 'vertical' }}
                            value={notes}
                            onChange={(event) => setNotes(event.target.value)}
                        />
                    </label>
                </div>
            </details>

            <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-sm">
                    <input
                        aria-label="planToIfcAutoForm"
                        checked={autoForm}
                        disabled={isSubmitting}
                        type="checkbox"
                        onChange={(event) => setAutoForm(event.target.checked)}
                    />
                    {t('labelPlanToIfcAutoForm')}
                </label>
                <Button
                    disabled={!canGenerate}
                    leftSection={<Icon aria-hidden="true" icon="auto_awesome" />}
                    loading={isSubmitting}
                    size={sizes.button.small}
                    onClick={() => onGenerate(name, files, notes, autoForm)}
                >
                    {autoForm ? t('buttonGenerateIfcAndForm') : t('buttonGenerateIfc')}
                </Button>
            </div>
        </div>
    );
};
